import { useCallback, useEffect, useState } from 'react';

import { ErroreApi } from '../api/client.ts';
import { utenti as apiUtenti, type DatiNuovoUtente } from '../api/scadenziario.ts';
import type { Ruolo, Utente } from '../api/tipi-dati.ts';
import { Dialogo } from '../componenti/Dialogo.tsx';
import {
  DialogoEliminazione,
  ZonaPericolosa,
  type BersaglioEliminazione,
} from '../componenti/DialogoEliminazione.tsx';
import { istante } from '../componenti/formato.ts';
import { useAutenticazione } from '../auth/contesto.tsx';
import {
  ETICHETTE_RUOLO,
  ORDINE_RUOLI,
  SPIEGAZIONE_RUOLO,
  eRuolo,
  puoAmministrare,
} from '../auth/ruoli.ts';

/**
 * Genera una password provvisoria leggibile ad alta voce.
 *
 * Serve perché l'amministratore la dovrà **dettare a un collega**: se contiene
 * `l`, `1`, `O` e `0` verrà trascritta male e il collega penserà di essere
 * bloccato fuori. Alfabeto senza caratteri ambigui, e la lunghezza compensa
 * con gli interessi l'alfabeto più corto.
 *
 * `crypto.getRandomValues` e non `Math.random()`: quest'ultimo non è
 * imprevedibile, ed è una password che apre l'applicazione aziendale.
 */
function generaPasswordProvvisoria(lunghezza = 16): string {
  const alfabeto = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
  const numeri = new Uint32Array(lunghezza);
  crypto.getRandomValues(numeri);

  // Il modulo introduce una distorsione trascurabile: 2^32 diviso 56 lascia
  // uno sbilanciamento di circa una parte su 76 milioni.
  return Array.from(numeri, (n) => alfabeto[n % alfabeto.length]).join('');
}

/**
 * Gestione degli utenti dell'ufficio.
 *
 * Gli utenti non si cancellano, si disattivano: le scadenze completate anni fa
 * devono continuare a dire chi le ha completate.
 */
export function Utenti() {
  // `ricarica` esiste già qui sotto come contatore per rileggere l'elenco: il
  // rinominato dice chiaramente che questo rilegge tutt'altro.
  const { utente: io, ricarica: rileggiUtenteCollegato } = useAutenticazione();

  const [elementi, setElementi] = useState<Utente[]>([]);
  const [mostraDisattivati, setMostraDisattivati] = useState(false);
  const [inCaricamento, setInCaricamento] = useState(true);
  const [errore, setErrore] = useState<string | null>(null);
  const [conferma, setConferma] = useState<string | null>(null);
  const [inForm, setInForm] = useState<Utente | null | undefined>(undefined);
  const [inReimposta, setInReimposta] = useState<Utente | null>(null);
  /** L'utente per cui è aperta la conferma di cancellazione definitiva. */
  const [daEliminare, setDaEliminare] = useState<BersaglioEliminazione | null>(null);
  const [ricarica, setRicarica] = useState(0);

  useEffect(() => {
    let annullato = false;
    setInCaricamento(true);

    apiUtenti
      .elenca(mostraDisattivati)
      .then((r) => { if (!annullato) { setElementi(r.elementi); setErrore(null); } })
      .catch((e: unknown) => {
        if (!annullato) setErrore(e instanceof Error ? e.message : 'Errore imprevisto');
      })
      .finally(() => { if (!annullato) setInCaricamento(false); });

    return () => { annullato = true; };
  }, [mostraDisattivati, ricarica]);

  /**
   * @param eroIo l'utente toccato è quello collegato. In quel caso non basta
   *   rileggere l'elenco: nome, email e ruolo che ha appena cambiato li sta
   *   guardando anche nell'intestazione e in Impostazioni, e quelli vengono dal
   *   contesto di autenticazione. Senza questa rilettura resterebbero i vecchi
   *   fino al prossimo cambio di pagina.
   */
  const dopoAzione = useCallback((messaggio: string, eroIo = false) => {
    setInForm(undefined);
    setInReimposta(null);
    setDaEliminare(null);
    setConferma(messaggio);
    setRicarica((n) => n + 1);
    if (eroIo) void rileggiUtenteCollegato();
  }, [rileggiUtenteCollegato]);

  async function cambiaStato(u: Utente) {
    setErrore(null);
    try {
      if (u.attivo) await apiUtenti.disattiva(u.id);
      else await apiUtenti.riattiva(u.id);
      dopoAzione(
        `${u.nome} ${u.attivo ? 'non ha più accesso' : 'può accedere di nuovo'}.`,
        u.id === io?.id,
      );
    } catch (e) {
      setErrore(e instanceof ErroreApi ? e.message : 'Errore imprevisto');
    }
  }

  // Rete di sicurezza: da quando la pagina è una sezione di «Impostazioni», il
  // permesso lo controlla già l'impalcatura là fuori. Resta perché è anche ciò
  // che dice a TypeScript che sotto `io` non è più `null`, e perché la pagina
  // non deve dipendere da chi la monta per non mostrare dati a chi non tocca.
  if (io === null || !puoAmministrare(io.ruolo)) {
    return (
      <>
        <div className="titolo-pagina"><h2>Utenti</h2></div>
        <div className="pannello">
          <p className="stato-vuoto">
            Questa sezione è riservata agli amministratori: da qui si danno e si tolgono
            gli accessi all'applicazione.
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      {/* `h2` e non `h1`: il titolo della pagina è «Impostazioni», qui sopra
          nell'impalcatura, e due `h1` nella stessa schermata farebbero perdere
          l'ordine a chi si muove per titoli con un lettore di schermo. */}
      <div className="titolo-pagina">
        <h2>Utenti</h2>
        <span className="conteggio">{elementi.length} in elenco</span>
        <button
          type="button"
          className="bottone bottone-principale azione-titolo"
          onClick={() => setInForm(null)}
        >
          <span aria-hidden="true">+</span> Nuovo utente
        </button>
      </div>

      <p className="aiuto" style={{ maxWidth: '70ch', marginBottom: 16 }}>
        Chi può entrare nello scadenziario. Gli <strong>amministratori</strong> gestiscono in
        più utenti, impostazioni e server di posta; i <strong>redattori</strong> tengono in
        ordine i dati — catalogo, anagrafiche e cancellazioni comprese — senza toccare le
        impostazioni; gli <strong>operatori</strong> fanno il lavoro quotidiano sulle scadenze.
        Nessuno si cancella: si toglie l'accesso, così le scadenze completate continuano a dire
        chi le ha completate.
      </p>

      {conferma !== null && (
        <div className="avviso-esito" role="status">
          {conferma}
          <button type="button" className="bottone-piatto" onClick={() => setConferma(null)}>Chiudi</button>
        </div>
      )}
      {errore !== null && <div className="avviso-errore" role="alert">{errore}</div>}

      <div className="filtri">
        <label className="interruttore">
          <input
            type="checkbox"
            checked={mostraDisattivati}
            onChange={(e) => setMostraDisattivati(e.target.checked)}
          />
          Mostra anche chi non ha più accesso
        </label>
      </div>

      <div className="pannello">
        {inCaricamento ? (
          <p className="stato-caricamento">Caricamento…</p>
        ) : (
          <div className="tabella-contenitore">
            <table className="azioni-centrate">
              <thead>
                <tr>
                  <th scope="col">Nome</th>
                  <th scope="col">Email</th>
                  <th scope="col">Ruolo</th>
                  <th scope="col">Avvisi</th>
                  <th scope="col" className="col-espandi">Ultimo accesso</th>
                  <th scope="col" className="col-azione">Azione</th>
                </tr>
              </thead>
              <tbody>
                {elementi.map((u) => (
                  <tr key={u.id} className={u.attivo ? '' : 'riga-annullata'}>
                    <td>
                      <span className="soggetto-nome">{u.nome}</span>
                      {u.id === io.id && <span className="tenue"> · sei tu</span>}
                      {!u.attivo && (
                        <span className="badge badge-annullata" style={{ marginLeft: 8 }}>
                          <span aria-hidden="true">⊘</span> Senza accesso
                        </span>
                      )}
                      {u.deveCambiarePassword && (
                        <span className="badge badge-in-scadenza" style={{ marginLeft: 8 }}>
                          <span aria-hidden="true">◔</span> Password provvisoria
                        </span>
                      )}
                    </td>
                    <td className="tipo-nome">{u.email}</td>
                    <td>{ETICHETTE_RUOLO[u.ruolo]}</td>
                    <td className="tipo-nome">{u.riceveNotifiche ? 'Sì' : 'No'}</td>
                    <td className="col-espandi data">
                      {u.ultimoAccesso === null
                        ? <span className="tenue">mai entrato</span>
                        : istante(u.ultimoAccesso)}
                    </td>
                    <td className="col-azione">
                      <div className="cella-azioni">
                        <button type="button" className="bottone" onClick={() => setInForm(u)}>
                          <span aria-hidden="true">✎</span> Modifica
                        </button>
                        <button type="button" className="bottone" onClick={() => setInReimposta(u)}>
                          Password
                        </button>
                        {/* Su sé stessi il backend rifiuta: meglio non offrire
                            nemmeno il pulsante, invece di far scoprire il
                            divieto con un errore. */}
                        {u.id !== io.id && (
                          <button type="button" className="bottone" onClick={() => void cambiaStato(u)}>
                            {u.attivo ? 'Togli accesso' : 'Riattiva'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {inForm !== undefined && (
        <DialogoUtente
          utente={inForm}
          sonoIo={inForm !== null && inForm.id === io.id}
          onChiudi={() => setInForm(undefined)}
          onSalvato={dopoAzione}
          onElimina={(u) => {
            setInForm(undefined);
            setDaEliminare({
              entita: 'utente',
              id: u.id,
              // L'email e non il nome: è unica, è corta da riscrivere, e
              // distingue due colleghi che si chiamano allo stesso modo —
              // che è esattamente lo scambio da evitare qui.
              nome: u.email,
              etichettaNome: "l'indirizzo email dell'utente",
            });
          }}
        />
      )}

      {inReimposta !== null && (
        <DialogoPassword
          utente={inReimposta}
          onChiudi={() => setInReimposta(null)}
          onFatto={dopoAzione}
        />
      )}

      {daEliminare !== null && (
        <DialogoEliminazione
          bersaglio={daEliminare}
          onChiudi={() => setDaEliminare(null)}
          // Un amministratore non può cancellare sé stesso, quindi qui
          // l'utente collegato non è mai quello sparito: nessuna rilettura.
          onEliminato={(messaggio) => dopoAzione(messaggio)}
        />
      )}
    </>
  );
}

/**
 * Copia la password negli appunti.
 *
 * La conferma «Copiata» dura qualche secondo e poi torna indietro: senza,
 * chi genera una seconda password non saprebbe se quel «Copiata» si riferisce
 * a quella nuova o è rimasto lì dalla precedente.
 */
function BottoneCopia({ testo }: { testo: string }) {
  const [copiato, setCopiato] = useState(false);

  useEffect(() => {
    if (!copiato) return;
    const timer = setTimeout(() => setCopiato(false), 2500);
    return () => clearTimeout(timer);
  }, [copiato]);

  // Un nuovo valore azzera la conferma: si riferiva a quello vecchio.
  useEffect(() => setCopiato(false), [testo]);

  return (
    <button
      type="button"
      className="bottone"
      onClick={() => {
        void navigator.clipboard.writeText(testo).then(
          () => setCopiato(true),
          () => setCopiato(false),
        );
      }}
    >
      {copiato ? 'Copiata' : 'Copia'}
    </button>
  );
}

function DialogoUtente({
  utente, sonoIo, onChiudi, onSalvato, onElimina,
}: {
  utente: Utente | null;
  sonoIo: boolean;
  onChiudi: () => void;
  onSalvato: (messaggio: string, eroIo?: boolean) => void;
  onElimina: (utente: Utente) => void;
}) {
  const nuovo = utente === null;

  const [nome, setNome] = useState(utente?.nome ?? '');
  const [email, setEmail] = useState(utente?.email ?? '');
  const [ruolo, setRuolo] = useState<Ruolo>(utente?.ruolo ?? 'OPERATORE');
  const [riceveNotifiche, setRiceveNotifiche] = useState(utente?.riceveNotifiche ?? true);
  const [password, setPassword] = useState(() => (nuovo ? generaPasswordProvvisoria() : ''));

  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  async function salva() {
    setErrore(null);
    setInCorso(true);

    try {
      if (nuovo) {
        const dati: DatiNuovoUtente = {
          email: email.trim(),
          nome: nome.trim(),
          ruolo,
          riceveNotifiche,
          passwordProvvisoria: password,
        };
        const creato = await apiUtenti.crea(dati);
        onSalvato(
          `${creato.nome} può entrare. Comunicagli la password provvisoria: dovrà cambiarla al primo accesso.`,
        );
      } else {
        await apiUtenti.modifica(utente.id, {
          email: email.trim(),
          nome: nome.trim(),
          ruolo,
          riceveNotifiche,
        });
        // Se ha appena modificato sé stesso, `sonoIo` fa rileggere l'utente
        // collegato: altrimenti il nome in alto a destra e l'indirizzo della
        // prova d'invio resterebbero quelli di prima.
        onSalvato(`${nome.trim()} aggiornato.`, sonoIo);
      }
    } catch (e) {
      setErrore(e instanceof ErroreApi ? e.message : 'Errore imprevisto');
      setInCorso(false);
    }
  }

  return (
    <Dialogo titolo={nuovo ? 'Nuovo utente' : 'Modifica utente'} aperto onChiudi={onChiudi}>
      {errore !== null && <div className="avviso-errore" role="alert">{errore}</div>}

      <form onSubmit={(e) => { e.preventDefault(); void salva(); }}>
        <div className="campo">
          <label htmlFor="u-nome">Nome e cognome <span className="obbligatorio">obbligatorio</span></label>
          <input
            id="u-nome"
            type="text"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            placeholder="es. Maria Bianchi"
            required
            autoFocus
          />
          <p className="aiuto">È il nome che comparirà nello storico delle scadenze.</p>
        </div>

        <div className="campo">
          <label htmlFor="u-email">Email <span className="obbligatorio">obbligatoria</span></label>
          <input
            id="u-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="es. maria.bianchi@azienda.it"
            required
          />
          <p className="aiuto">
            Serve per entrare, ed è l'indirizzo a cui arrivano gli avvisi di scadenza.
          </p>
        </div>

        <div className="campo">
          <label htmlFor="u-ruolo">Ruolo</label>
          {/*
            Le voci si generano dall'elenco dei ruoli, in ordine di gerarchia.
            Scritte a mano, un ruolo nuovo esisterebbe nel database e nell'API
            senza comparire qui: assegnabile da nessuno.
          */}
          <select
            id="u-ruolo"
            value={ruolo}
            onChange={(e) => {
              // `eRuolo` e non un `as`: un cast è una promessa fatta al
              // compilatore, e sul contenuto di un menu conviene mantenerla a
              // runtime.
              if (eRuolo(e.target.value)) setRuolo(e.target.value);
            }}
            disabled={sonoIo}
          >
            {ORDINE_RUOLI.map((r) => (
              <option key={r} value={r}>
                {ETICHETTE_RUOLO[r]}
              </option>
            ))}
          </select>
          {/*
            ⚠️ **Una mappa e non un ternario.** Fino al 28 agosto 2026 qui c'era
            un `? :` a due rami: qualunque terzo ruolo sarebbe caduto nella
            descrizione dell'operatore, dicendo il falso all'amministratore
            proprio mentre gli assegna un permesso. Compilava, non lanciava, e
            si leggeva credendoci.
          */}
          <p className="aiuto">
            {sonoIo
              ? 'Non puoi cambiare il tuo ruolo: se ti togliessi i permessi da solo, non avresti più modo di rimediare — nemmeno passando a redattore. Fattelo cambiare da un collega amministratore.'
              : SPIEGAZIONE_RUOLO[ruolo]}
          </p>
        </div>

        <label className="interruttore" style={{ marginBottom: 16 }}>
          <input
            type="checkbox"
            checked={riceveNotifiche}
            onChange={(e) => setRiceveNotifiche(e.target.checked)}
          />
          Riceve gli avvisi di scadenza via email
        </label>

        {nuovo && (
          <div className="campo">
            <label htmlFor="u-pwd">Password provvisoria</label>
            <div className="campo-affiancato">
              <input
                id="u-pwd"
                type="text"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                minLength={12}
                required
                style={{ flex: 1, fontFamily: 'var(--mono)' }}
              />
              <button
                type="button"
                className="bottone"
                onClick={() => setPassword(generaPasswordProvvisoria())}
              >
                Genera
              </button>
              <BottoneCopia testo={password} />
            </div>
            <p className="aiuto">
              Copia e comunica questa password alla persona per cui è stato creato l'utente,
              appena avverrà il primo accesso, verrà invitato a cambiarla.
            </p>
          </div>
        )}

        <div className="dialogo-azioni">
          <button type="submit" className="bottone bottone-principale" disabled={inCorso}>
            {inCorso ? 'Salvataggio…' : nuovo ? 'Crea utente' : 'Salva modifiche'}
          </button>
          <button type="button" className="bottone-piatto" onClick={onChiudi} disabled={inCorso}>
            Annulla
          </button>
        </div>
      </form>

      {/*
        Compare anche sulla propria scheda, al contrario di «Togli accesso» che
        su sé stessi non viene nemmeno mostrato. Non è un'incoerenza: lì il
        rifiuto arriverebbe solo dopo il clic, qui invece il dialogo interroga
        prima il backend, che risponde `AUTO_ELIMINAZIONE` con la spiegazione
        già scritta. Il risultato è che chi ci prova legge *perché* non si può —
        «perderesti l'accesso mentre lo stai usando» — invece di trovare un
        pulsante misteriosamente assente.
      */}
      {utente !== null && (
        <ZonaPericolosa
          spiegazione={
            "Cancella l'utente e ogni suo riferimento. Le scadenze che ha completato restano, ma non " +
            'diranno più chi le ha completate; il registro operazioni invece continua a nominarlo. ' +
            "Quasi sempre la risposta giusta è «Togli accesso»: chiude la porta e lascia intatto lo storico. " +
            'Questa serve per il collega inserito per errore o mai entrato.'
          }
          onElimina={() => onElimina(utente)}
        />
      )}
    </Dialogo>
  );
}

function DialogoPassword({
  utente, onChiudi, onFatto,
}: {
  utente: Utente;
  onChiudi: () => void;
  onFatto: (messaggio: string) => void;
}) {
  const [password, setPassword] = useState(() => generaPasswordProvvisoria());
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  async function reimposta() {
    setErrore(null);
    setInCorso(true);

    try {
      await apiUtenti.reimpostaPassword(utente.id, password);
      onFatto(`Password di ${utente.nome} reimpostata. Comunicagliela: dovrà cambiarla al primo accesso.`);
    } catch (e) {
      setErrore(e instanceof ErroreApi ? e.message : 'Errore imprevisto');
      setInCorso(false);
    }
  }

  return (
    <Dialogo titolo={`Password di ${utente.nome}`} aperto onChiudi={onChiudi}>
      {errore !== null && <div className="avviso-errore" role="alert">{errore}</div>}

      {/* Lo stacco sotto è voluto: `.aiuto` non ha margine inferiore, e senza
          questo la spiegazione si appiccica all'etichetta del campo. 16px è lo
          stesso passo che `.dialogo .campo` usa fra un campo e l'altro. */}
      <p className="aiuto" style={{ marginTop: 0, marginBottom: 16 }}>
        Da usare quando un collega ha dimenticato la password. Quella attuale smette
        subito di funzionare e le sessioni aperte vengono chiuse: se stai reimpostando
        perché temi che qualcuno l'abbia scoperta, lasciarle vive vanificherebbe tutto.
      </p>

      <form onSubmit={(e) => { e.preventDefault(); void reimposta(); }}>
        <div className="campo">
          <label htmlFor="u-pwd-new">Nuova password provvisoria</label>
          <div className="campo-affiancato">
            <input
              id="u-pwd-new"
              type="text"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              minLength={12}
              required
              style={{ flex: 1, fontFamily: 'var(--mono)' }}
            />
            <button
              type="button"
              className="bottone"
              onClick={() => setPassword(generaPasswordProvvisoria())}
            >
              Genera
            </button>
            <BottoneCopia testo={password} />
          </div>
          <p className="aiuto">
            Copia e comunica questa password a {utente.nome}: al primo accesso verrà
            invitato a cambiarla.
          </p>
        </div>

        <div className="dialogo-azioni">
          <button type="submit" className="bottone bottone-principale" disabled={inCorso}>
            {inCorso ? 'Salvataggio…' : 'Reimposta password'}
          </button>
          <button type="button" className="bottone-piatto" onClick={onChiudi} disabled={inCorso}>
            Annulla
          </button>
        </div>
      </form>
    </Dialogo>
  );
}
